import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  ButtonIcon,
  ButtonSelect,
  Icon,
  MenuNested,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';

import { SORT_DATE_OPTIONS, SORT_OPTIONS } from './constants';
import Dropdown from './Dropdown';
import invertedMessages from './messages';
import { checkDateStringExist } from './utils';

import './SortFilter.css';

function SortFilter({
  filterName,
  options,
  headerTitle,
  placeholder,
  columnValue,
  sortValue,
  setSelectedColumnOption,
  setSortDirectionOption,
  resetFilter,
  isDisabled,
  isButtonKindIcon,
  isInMore,
}) {
  const [columnOptions, setColumnOptions] = useState([]);
  const [sortOptions, setSortOptions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSortOption, setSelectedSortOption] = useState(null);
  const l = useInvertedLocalize(invertedMessages);

  // to set the intial options for columns from props
  useEffect(() => {
    if (columnValue && sortValue && options.length > 0) {
      const defaultOptions = options.map((option) => ({
        ...option,
        isChecked: option.columnName === columnValue,
      }));

      setColumnOptions(defaultOptions);
      const selectedItem = defaultOptions?.find(
        (item) => item.isChecked === true
      );

      if (selectedItem) {
        setSelectedOption(selectedItem);
      }

      const sortOptionsList =
        selectedItem && checkDateStringExist(selectedItem.label)
          ? SORT_DATE_OPTIONS
          : SORT_OPTIONS;

      const defaultSortOptions = sortOptionsList.map((option) => ({
        ...option,
        isChecked: option.value === sortValue,
      }));
      const appliedSortOption = defaultSortOptions.find(
        (item) => item.isChecked === true
      );

      setSortOptions(defaultSortOptions);
      setSelectedSortOption(appliedSortOption);
    } else {
      setColumnOptions(options);
      if (selectedOption) setSelectedOption(null);
      if (selectedSortOption) setSelectedSortOption(null);
    }
  }, [options, columnValue]);

  // update the selected column and sort direction
  useEffect(() => {
    if (selectedOption && setSelectedColumnOption) {
      setSelectedColumnOption(selectedOption);
    }

    if (selectedSortOption && setSortDirectionOption) {
      setSortDirectionOption(selectedSortOption);
    }
  }, [selectedOption, selectedSortOption]);

  /**
   *
   * @param {*} selectedItem(object) - Selected column details
   */
  const handleChange = (selectedItem) => {
    setColumnOptions((currentOptions) =>
      currentOptions.map((option) => ({
        ...option,
        isChecked: option.value === selectedItem.value,
      }))
    );
    setSelectedOption(selectedItem);
    const sortOptionsList = checkDateStringExist(selectedItem.label)
      ? SORT_DATE_OPTIONS
      : SORT_OPTIONS;
    const appliedSortOption = sortOptionsList.find(
      (item) => item.isChecked === true
    );

    setSortOptions(sortOptionsList);
    setSelectedSortOption(appliedSortOption);
  };

  /**
   *
   * @param {*} selectedItem(object) - Selected sort direction details (ASC/DESC)
   */
  const handleSortChange = (selectedItem) => {
    setSortOptions((currentOptions) =>
      currentOptions.map((option) => ({
        ...option,
        isChecked: option.value === selectedItem.value,
      }))
    );
    setSelectedSortOption(selectedItem);
  };

  // to filter with the search text
  const filteredOptions = columnOptions?.filter((option) =>
    option.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const getDropdownMenu = () => (
    <>
      {!selectedOption && (
        <Dropdown
          options={filteredOptions}
          headerLabel={headerTitle}
          placeholder={placeholder}
          isHeaderRequired
          handleChange={handleChange}
          searchText={searchText}
          setSearchText={setSearchText}
        />
      )}
      {selectedOption && (
        <Box className='SortFilter-menu'>
          <MenuNested
            classNames={{
              dropdownClassName: 'SortFilter-columnList',
            }}
            placement='bottom-start'
            tabIndex={-1}
            ariaLabel={l('Sort')}
            trigger={
              <ButtonSelect
                className='SortFilter-columnList--button'
                classNames={{
                  contentContainerClassName:
                    'SortFilter-columnList--buttonContainer',
                }}
                type='button'
                ariaLabel={selectedSortOption.label}
                label={
                  <Typography
                    kind='body3'
                    color='--color-foreground-secondary-bold'>
                    {selectedOption.label}
                  </Typography>
                }
                size='small'
              />
            }>
            <Dropdown
              options={filteredOptions}
              isHeaderRequired={false}
              handleChange={handleChange}
              searchText={searchText}
              setSearchText={setSearchText}
              headerLabel={headerTitle}
              placeholder={placeholder}
            />
          </MenuNested>

          {selectedSortOption && (
            <MenuNested
              classNames={{
                dropdownClassName: 'SortFilter-sortDirection',
              }}
              placement='bottom-start'
              tabIndex={-1}
              ariaLabel={l('Sort direction')}
              trigger={
                <ButtonSelect
                  type='button'
                  ariaLabel={l('Sort')}
                  label={
                    <Typography
                      kind='body3'
                      color='--color-foreground-secondary-bold'>
                      {selectedSortOption.label}
                    </Typography>
                  }
                  size='small'
                />
              }>
              <Dropdown
                options={sortOptions}
                isHeaderRequired={false}
                showSearchBar={false}
                handleChange={handleSortChange}
              />
            </MenuNested>
          )}
          <ButtonIcon
            size='small'
            kind='ghost'
            ariaLabel={`${l('Clear')} ${l('Sort')}`}
            icon={<Icon kind='close' color='neutral-700' size='xSmall' />}
            onClick={() => {
              setSelectedOption(null);
              setSelectedSortOption(null);
              setSelectedColumnOption(null);
              setSortDirectionOption(null);
              setColumnOptions((currentOptions) =>
                currentOptions.map((option) => ({
                  ...option,
                  isChecked: false,
                }))
              );
              resetFilter();
            }}
          />
        </Box>
      )}
    </>
  );

  if (isInMore) {
    return <Box className='SortFilter'>{getDropdownMenu()}</Box>;
  }

  return (
    <MenuNested
      placement='bottom-start'
      classNames={{
        dropdownClassName: 'SortFilter',
      }}
      tabIndex={-1}
      ariaLabel={l('Sort')}
      trigger={
        <ButtonSelect
          kind={isButtonKindIcon ? 'rectangle' : 'icon'}
          size='medium'
          prefixIcon={<Icon kind='Sort' />}
          label={filterName}
          ariaLabel={l('Sort')}
          BadgeComp={
            selectedOption !== null ? (
              <Badge
                kind='dot'
                color='primary'
                size='small'
                className='SortFilter-badge'
              />
            ) : null
          }
          isDisabled={isDisabled}
          isVisualCueEnabled={false}
        />
      }>
      {getDropdownMenu()}
    </MenuNested>
  );
}

SortFilter.propTypes = {
  filterName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
      isChecked: PropTypes.bool,
      isIconFillDisabled: PropTypes.bool,
      columnName: PropTypes.string,
    })
  ).isRequired,
  columnValue: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  setSelectedColumnOption: PropTypes.func.isRequired,
  setSortDirectionOption: PropTypes.func.isRequired,
  resetFilter: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isInMore: PropTypes.bool,
  isButtonKindIcon: PropTypes.bool,
  headerTitle: PropTypes.string,
  placeholder: PropTypes.string,
};

SortFilter.defaultProps = {
  filterName: '',
  headerTitle: '',
  placeholder: '',
  isDisabled: false,
  isInMore: false,
  columnValue: null,
  sortValue: null,
  isButtonKindIcon: false,
};

export default SortFilter;
